import { resolvePublicSiteUrl } from "@multi-tenant-cms/seo-runtime";
import { applyTrailingSlashPreference } from "@multi-tenant-cms/seo-runtime/deployment-url";

import { normalizeRequestPath } from "@/lib/path";

const TRAILING_SLASH_RE = /\/+$/;

/**
 * Path prefix for this deployment (`NEXT_PUBLIC_BASE_PATH`), normalized without trailing slash.
 * Empty on the global root VM.
 */
export function deploymentBasePath(): string {
	const raw = process.env.NEXT_PUBLIC_BASE_PATH?.trim() ?? "";
	if (!raw || raw === "/") {
		return "";
	}
	const withLeading = raw.startsWith("/") ? raw : `/${raw}`;
	return withLeading.replace(TRAILING_SLASH_RE, "");
}

/**
 * Pathname prefix from Settings → General Site URL
 * (e.g. `https://figsflow.com/us` → `/us`). Empty when host-only / unset.
 * Used for public branding asset hrefs — does not read `NEXT_PUBLIC_BASE_PATH`.
 */
export function publicPathPrefixFromSiteUrl(
	siteUrl: string | null | undefined
): string {
	const raw = siteUrl?.trim();
	if (!raw) {
		return "";
	}
	try {
		const pathname = new URL(raw).pathname.replace(TRAILING_SLASH_RE, "");
		if (!pathname || pathname === "/") {
			return "";
		}
		return pathname.startsWith("/") ? pathname : `/${pathname}`;
	} catch {
		return "";
	}
}

/**
 * Prefix a same-origin public asset path with the Site URL pathname.
 * Idempotent (no double-prefix). Never prefixes `/robots.txt` (host-level only).
 */
export function prefixPublicAssetPath(
	path: string,
	siteUrl: string | null | undefined
): string {
	const normalized = path.startsWith("/") ? path : `/${path}`;
	if (normalized === "/robots.txt" || normalized.startsWith("/robots.txt?")) {
		return normalized;
	}
	const prefix = publicPathPrefixFromSiteUrl(siteUrl);
	if (!prefix) {
		return normalized;
	}
	if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
		return normalized;
	}
	return `${prefix}${normalized}`;
}

export function isRegionalDeployment(): boolean {
	return deploymentBasePath().length > 0;
}

/**
 * Fully-qualified public origin for this deployment, e.g. `https://taxule.com/uk`.
 * Prefers `NEXT_PUBLIC_DOMAIN` + base path; falls back to `requestOrigin` in dev.
 */
export function resolveDeploymentPublicOrigin(
	requestOrigin?: string | null
): string {
	const domain = process.env.NEXT_PUBLIC_DOMAIN?.trim();
	const base = deploymentBasePath();
	if (domain) {
		return `https://${domain.replace(/^https?:\/\//, "").replace(TRAILING_SLASH_RE, "")}${base}`;
	}
	const fallback = requestOrigin?.trim().replace(TRAILING_SLASH_RE, "") ?? "";
	if (!fallback) {
		return "";
	}
	if (!base) {
		return fallback;
	}
	try {
		const url = new URL(fallback);
		return `${url.origin}${base}`;
	} catch {
		return `${fallback}${base}`;
	}
}

/**
 * Strip the deployment base path so CMS routing / redirects match stored paths
 * (stored without the regional prefix per publish fanout contract).
 */
export function stripDeploymentBasePath(pathname: string): string {
	const base = deploymentBasePath();
	const normalized = normalizeRequestPath(pathname);
	if (!base) {
		return normalized;
	}
	if (normalized === base) {
		return "/";
	}
	if (normalized.startsWith(`${base}/`)) {
		const rest = normalized.slice(base.length);
		return rest.length > 0 ? rest : "/";
	}
	return normalized;
}

/** Prefix a CMS-relative path with the deployment base path (edge cache purge, absolute links). */
export function prependDeploymentBasePath(path: string): string {
	const base = deploymentBasePath();
	const normalized = path.startsWith("/") ? path : `/${path}`;
	if (!base) {
		return normalized;
	}
	if (normalized === base || normalized.startsWith(`${base}/`)) {
		return normalized;
	}
	if (normalized === "/") {
		return `${base}/`;
	}
	return `${base}${normalized}`.replace(/\/+/g, "/");
}

/**
 * Public Location for 301/302 responses after CMS path resolution.
 * Absolute URLs pass through unchanged. Relative CMS paths get the deployment
 * prefix, then the site trailing-slash preference when enabled.
 *
 * When `trailingSlash` is false/omitted (default for existing tenants), output
 * matches the historical strip form — no behaviour change for those sites.
 */
export function publicRedirectLocation(
	location: string,
	options?: { trailingSlash?: boolean }
): string {
	if (/^https?:\/\//i.test(location.trim())) {
		return location.trim();
	}
	const prefixed = prependDeploymentBasePath(location);
	if (options?.trailingSlash !== true) {
		return prefixed;
	}
	return applyTrailingSlashPreference(prefixed, true);
}

/** Active region prefixes from `SITEMAP_REGIONS` (global root VM only). */
export function parseSitemapRegionPrefixes(): string[] {
	return (process.env.SITEMAP_REGIONS ?? "")
		.split(",")
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0)
		.map((entry) => {
			const withLeading = entry.startsWith("/") ? entry : `/${entry}`;
			return withLeading.replace(TRAILING_SLASH_RE, "");
		});
}

export type DeploymentSiteUrlOptions = {
	crawlDockerHostAlias?: string;
	preservePath?: boolean;
};

/**
 * Public site origin for sitemap / robots / glossary / canonicals.
 *
 * When `NEXT_PUBLIC_DOMAIN` is set (regional VM), the deployment https origin wins.
 * Otherwise prefer Site SEO `site_url` from the database (see `resolvePublicSiteUrl`).
 */
export function resolveDeploymentPublicSiteUrl(
	configuredSiteUrl: string | null | undefined,
	requestOrigin: string | null | undefined,
	options?: DeploymentSiteUrlOptions
): string {
	if (process.env.NEXT_PUBLIC_DOMAIN?.trim()) {
		const deploymentOrigin = resolveDeploymentPublicOrigin(requestOrigin);
		if (deploymentOrigin) {
			return deploymentOrigin;
		}
	}

	const configured = configuredSiteUrl?.trim() ?? "";
	if (configured) {
		try {
			const configuredUrl = new URL(configured);
			if (
				configuredUrl.protocol === "http:" ||
				configuredUrl.protocol === "https:"
			) {
				return resolvePublicSiteUrl(configured, requestOrigin, options);
			}
		} catch {
			// Fall back to deployment origin / request origin handling below.
		}
	}
	return resolvePublicSiteUrl(configuredSiteUrl, requestOrigin, options);
}

export function metadataBaseUrlFromTheme(
	themeSiteUrl: string | undefined,
	requestOrigin?: string | null
): URL | undefined {
	const deploymentOrigin = resolveDeploymentPublicOrigin(requestOrigin);
	if (deploymentOrigin) {
		try {
			return new URL(`${deploymentOrigin}/`);
		} catch {
			// fall through
		}
	}
	const siteUrl = themeSiteUrl?.trim();
	if (!siteUrl) {
		return undefined;
	}
	try {
		return new URL(siteUrl.endsWith("/") ? siteUrl : `${siteUrl}/`);
	} catch {
		return undefined;
	}
}

/**
 * Join Site SEO `site_url` (which may include a regional pathname) with a
 * CMS-relative path and optional query string.
 */
export function joinPublicSiteUrlPath(
	siteUrl: string,
	pathWithOptionalQuery: string
): string {
	const siteBase = siteUrl.trim().replace(TRAILING_SLASH_RE, "");
	if (!siteBase) {
		return pathWithOptionalQuery;
	}

	const queryIndex = pathWithOptionalQuery.indexOf("?");
	const pathname =
		queryIndex >= 0
			? pathWithOptionalQuery.slice(0, queryIndex)
			: pathWithOptionalQuery;
	const query = queryIndex >= 0 ? pathWithOptionalQuery.slice(queryIndex) : "";

	const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;

	try {
		const base = new URL(`${siteBase}/`);
		const combined =
			`${base.pathname.replace(TRAILING_SLASH_RE, "")}${normalizedPath}`.replace(
				/\/+/g,
				"/"
			);
		base.pathname = combined;
		return `${base.origin}${base.pathname}${query}`;
	} catch {
		return `${siteBase}${normalizedPath}${query}`.replace(/(?<!:)\/{2,}/g, "/");
	}
}

/**
 * Absolute public href for archive entries, filters, and pagination.
 * Uses Site SEO `site_url` when set; otherwise keeps deployment-prefixed relative paths.
 */
export function buildPublicContentHref(
	resolvedSiteUrl: string,
	cmsRelativePath: string,
	options?: { trailingSlash?: boolean }
): string {
	const cmsPath = cmsRelativePath.startsWith("/")
		? cmsRelativePath
		: `/${cmsRelativePath}`;
	const deployPrefix = deploymentBasePath();
	const siteBase = resolvedSiteUrl.trim().replace(TRAILING_SLASH_RE, "");
	const trailingSlash = options?.trailingSlash === true;

	if (!siteBase) {
		const relative = deployPrefix
			? prependDeploymentBasePath(cmsPath)
			: cmsPath;
		return applyTrailingSlashPreference(relative, trailingSlash);
	}

	let contentPath = cmsPath;
	if (
		deployPrefix &&
		!siteBase.endsWith(deployPrefix) &&
		!cmsPath.startsWith(`${deployPrefix}/`)
	) {
		contentPath = prependDeploymentBasePath(cmsPath);
	}

	return applyTrailingSlashPreference(
		joinPublicSiteUrlPath(siteBase, contentPath),
		trailingSlash
	);
}

/** Rewrite sitemap `<loc>` values when DB site URL omits the deployment base path. */
export function prefixSitemapLocUrls(
	xml: string,
	configuredOrigin: string
): string {
	const base = deploymentBasePath();
	if (!base) {
		return xml;
	}
	const origin = configuredOrigin.replace(TRAILING_SLASH_RE, "");
	const prefixedOrigin = origin.endsWith(base) ? origin : `${origin}${base}`;
	// `absolutePublicUrl` already applies `NEXT_PUBLIC_BASE_PATH` on regional VMs.
	if (
		xml.includes(`<loc>${prefixedOrigin}/`) ||
		xml.includes(`<loc>${prefixedOrigin}<`)
	) {
		return xml;
	}
	if (origin === prefixedOrigin) {
		return xml;
	}
	return xml
		.replaceAll(`<loc>${origin}/`, `<loc>${prefixedOrigin}/`)
		.replaceAll(`<loc>${origin}<`, `<loc>${prefixedOrigin}<`);
}
